import React, { useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { useSelector } from 'react-redux';

import { HeaderProgram, PortalHeader } from './utils/utils';
import { getReceiptEnvanter, loginMikrokom, sendReceiptEnvanter } from '../module/mikrokom/utils/mikrokomRequest';
import Loading from '../Loading';

const extractInventoryResponse = (response) => {
    const candidates = [response?.data?.data?.data, response?.data?.data, response?.data];

    for (const candidate of candidates) {
        if (Array.isArray(candidate?.rows)) {
            return {
                envanterTur: candidate.envanterTur,
                rows: candidate.rows,
                rowCount: candidate.rowCount ?? candidate.rows.length
            };
        }

        if (Array.isArray(candidate)) {
            return {
                envanterTur: undefined,
                rows: candidate,
                rowCount: candidate.length
            };
        }
    }

    return {
        envanterTur: undefined,
        rows: [],
        rowCount: 0
    };
};

const extractResponseMessage = (response) => response?.data?.data?.message || response?.data?.message || '';

const trAmountFormatter = new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const trQuantityFormatter = new Intl.NumberFormat('tr-TR', {
    maximumFractionDigits: 6
});

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const stringValue = String(value);
    const normalizedAmount = stringValue.includes(',') ? stringValue.replace(/\./g, '').replace(',', '.') : stringValue;
    const amount = Number(normalizedAmount);

    if (Number.isNaN(amount)) return value;

    return trAmountFormatter.format(amount);
};

const formatQuantity = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const stringValue = String(value).trim();
    const normalizedAmount = stringValue.includes(',') ? stringValue.replace(/\./g, '').replace(',', '.') : stringValue;
    const amount = Number(normalizedAmount);

    if (Number.isNaN(amount)) return value;

    return trQuantityFormatter.format(amount);
};

const withFallback = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const formatInventoryDate = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const dateText = String(value).trim();
    const dateParts = dateText.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);

    if (dateParts) {
        const [, year, month, day] = dateParts;
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    }

    return dateText;
};

const inventoryUnitLabels = {
    TNE: 'ton',
    BX: 'Kutu',
    LTR: 'lt',
    C62: 'Adet',
    KGM: 'kg',
    KJO: 'kJ',
    GRM: 'g',
    MGM: 'mg',
    NT: 'Net Ton',
    GT: 'Gross Ton',
    MTR: 'm',
    MMT: 'mm',
    KTM: 'km',
    MLT: 'ml',
    MMQ: 'mm3',
    CLT: 'cl',
    CMK: 'cm2',
    CMQ: 'cm3',
    CMT: 'cm',
    DMK: 'dm2',
    MTK: 'm2',
    MTQ: 'm3',
    DAY: 'Gün',
    MON: 'Ay',
    PA: 'Paket',
    KWH: 'KWH',
    MWH: 'MWH',
    ANN: 'Yıl',
    HUR: 'Saat',
    D61: 'Dakika',
    D62: 'Saniye',
    CCT: 'Ton baş.taşıma kap.',
    D30: 'Brüt kalori',
    D40: '1000 lt',
    LPA: 'saf alkol lt',
    B32: 'kg.m2',
    NCL: 'hücre adet',
    PR: 'Çift',
    R9: '1000 m3',
    SET: 'Set',
    T3: '1000 adet',
    Q37: 'SCM',
    Q39: 'NCM',
    J39: 'mmBTU',
    G52: 'CM3',
    DZN: 'Düzine',
    CT: 'Karat'
};

const formatInventoryUnit = (unitCode) => inventoryUnitLabels[String(unitCode || '').trim()] || unitCode || '';

const inventoryTypeOptions = [
    { label: 'Açılış', value: '1' },
    { label: 'Kapanış', value: '2' }
];

const buildPeriodOptions = (company) => {
    if (!company) return [];

    const rawPeriods = company.periods || company.periodList || company.periodsList;

    if (Array.isArray(rawPeriods) && rawPeriods.length > 0) {
        return rawPeriods
            .map((period) => {
                const value = period.periodId ?? period.value ?? period.id;
                const label = period.periodTitle ?? period.title ?? period.label ?? period.name ?? value;

                return value
                    ? {
                          label: String(label),
                          value: String(value)
                      }
                    : null;
            })
            .filter(Boolean);
    }

    if (company.periodId) {
        return [
            {
                label: String(company.periodTitle || company.periodId),
                value: String(company.periodId)
            }
        ];
    }

    return [];
};

export default function MikrokomInventoryPage() {
    const toast = useRef(null);
    const { selectedCompany } = useSelector((state) => state.userCompany);

    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [envanterTur, setEnvanterTur] = useState('1');
    const [inventoryRows, setInventoryRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendDialogVisible, setSendDialogVisible] = useState(false);
    const [enteredBy, setEnteredBy] = useState('');

    const selectedModule = useMemo(() => selectedCompany?.modules?.find((module) => module?.setting?.settings?.program?.title?.includes('Mikrokom')), [selectedCompany]);
    const programReq = selectedModule?.setting?.settings?.program?.requirements?.[0] || {};
    const mikrokomCompanies = useMemo(() => selectedModule?.setting?.settings?.program?.mikrokomCompanies || [], [selectedModule]);
    const selectedMikrokomCompany = useMemo(() => mikrokomCompanies.find((company) => company.companyCode === selectedCompanyId), [mikrokomCompanies, selectedCompanyId]);
    const selectedTaxNumber = selectedMikrokomCompany?.taxId || selectedMikrokomCompany?.taxNumber || selectedMikrokomCompany?.VknTckn || selectedMikrokomCompany?.vknTckn || '';
    const periodOptions = useMemo(() => buildPeriodOptions(selectedMikrokomCompany), [selectedMikrokomCompany]);

    const inventoryDetailRows = useMemo(
        () =>
            inventoryRows.map((item, index) => ({
                ...item,
                _rowKey: `${item.EntryNumber || 'entry'}-${item.LineNumber || 'line'}-${item.AccountSubID || item.AccountMainID || 'account'}-${index}`
            })),
        [inventoryRows]
    );

    const sendEnabled = inventoryRows.length > 0 && !!selectedCompanyId && !!selectedPeriodId && !!envanterTur && !loading && !sendLoading;

    const resetInventoryRows = () => {
        setInventoryRows([]);
        setRowCount(0);
        setGlobalFilter(null);
        setSendDialogVisible(false);
        setEnteredBy('');
    };

    const handleCompanyChange = (event) => {
        setSelectedCompanyId(event.value);
        setSelectedPeriodId(null);
        resetInventoryRows();
    };

    const handlePeriodChange = (event) => {
        setSelectedPeriodId(event.value);
        resetInventoryRows();
    };

    const handleInventoryTypeChange = (event) => {
        setEnvanterTur(event.value);
        resetInventoryRows();
    };

    const getFreshMikrokomToken = async () => {
        if (!programReq?.username || !programReq?.password || !programReq?.domainCode) {
            throw new Error('Mikrokom giriş bilgileri bulunamadı.');
        }

        const loginRes = await loginMikrokom(programReq.username, programReq.password, programReq.domainCode);
        const token = loginRes?.data?.data;

        if (!token) {
            throw new Error('Mikrokom oturumu alınamadı.');
        }

        return token;
    };

    const handleLoadInventory = async () => {
        if (!selectedCompanyId) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen şirket seçiniz.', life: 3000 });
            return;
        }

        if (!selectedPeriodId) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen dönem seçiniz.', life: 3000 });
            return;
        }

        setLoading(true);

        try {
            const token = await getFreshMikrokomToken();
            const response = await getReceiptEnvanter({
                token,
                companyId: selectedCompanyId,
                periodId: selectedPeriodId,
                envanterTur
            });
            const data = extractInventoryResponse(response);

            setInventoryRows(data.rows);
            setRowCount(data.rowCount);
            toast.current.show({
                severity: data.rows.length > 0 ? 'success' : 'info',
                summary: data.rows.length > 0 ? 'Başarılı' : 'Bilgi',
                detail: extractResponseMessage(response) || (data.rows.length > 0 ? `${data.rowCount} adet envanter kaydı getirildi.` : 'Envanter kaydı bulunamadı.'),
                life: 3000
            });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || error?.message || 'Envanter listesi getirilemedi.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitSend = async (directSendValue) => {
        if (!enteredBy.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen gönderen kişi bilgisini giriniz.', life: 3000 });
            return;
        }

        if (!selectedCompanyId || !selectedPeriodId || inventoryRows.length === 0) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Gönderim için envanter listesini getiriniz.', life: 3000 });
            return;
        }

        if (!selectedTaxNumber) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'VKN/TCKN bilgisi bulunamadı.', life: 3000 });
            return;
        }

        const webServiceReq = selectedMikrokomCompany?.webService?.requirements || {};

        if (!selectedMikrokomCompany?.webService?.url || !webServiceReq?.username || !webServiceReq?.password) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Web servis bilgileri bulunamadı.', life: 3000 });
            return;
        }

        setSendLoading(true);

        try {
            const token = await getFreshMikrokomToken();
            const response = await sendReceiptEnvanter({
                token,
                companyId: selectedCompanyId,
                periodId: selectedPeriodId,
                envanterTur,
                VknTckn: selectedTaxNumber,
                enteredBy: enteredBy.trim(),
                eDefterSubeAdi: selectedMikrokomCompany.eDefterSubeAdi,
                eDefterSubeKodu: selectedMikrokomCompany.eDefterSubeKodu,
                webServiceUrl: selectedMikrokomCompany.webService.url,
                webServiceUsername: webServiceReq.username,
                webServicePassword: webServiceReq.password,
                directSend: directSendValue
            });

            toast.current.show({ severity: 'success', summary: 'Başarılı', detail: extractResponseMessage(response) || 'Envanter gönderildi.', life: 3000 });
            setSendDialogVisible(false);
            setEnteredBy('');
        } catch (error) {
            error.code === 'ERR_NETWORK'
                ? toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İstek zaman aşımına uğradı', life: 3000 })
                : toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || error?.message || 'Envanter gönderilemedi.', life: 3000 });
        } finally {
            setSendLoading(false);
        }
    };

    const accountMainTemplate = (rowData) => (
        <div className="flex flex-column gap-1">
            <span className="font-bold">{withFallback(rowData.AccountMainID)}</span>
            <span>{withFallback(rowData.AccountMainDescription)}</span>
        </div>
    );

    const accountSubTemplate = (rowData) => (
        <div className="flex flex-column gap-1">
            <span className="font-bold">{withFallback(rowData.AccountSubID)}</span>
            <span>{withFallback(rowData.AccountSubDescription)}</span>
        </div>
    );

    const measurableTemplate = (rowData) => {
        const quantity = formatQuantity(rowData.measurableQuantity);
        const unit = formatInventoryUnit(rowData.measurableUnitOfMeasure);

        return <span>{[quantity, unit].filter((item) => item && item !== '-').join(' ') || '-'}</span>;
    };

    const detailHeader = (
        <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="flex flex-column gap-1">
                <h4 className="m-0">Envanter Kayıtları</h4>
                {inventoryRows.length > 0 && (
                    <div className="flex align-items-center gap-2">
                        <Tag className="text-white" severity="info" value={`${rowCount} kayıt`} />
                        {selectedMikrokomCompany?.companyName && <span className="text-sm text-color-secondary">{selectedMikrokomCompany.companyName}</span>}
                    </div>
                )}
            </div>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(event) => setGlobalFilter(event.target.value)} placeholder="Ara..." />
            </span>
        </div>
    );

    if (loading) {
        return (
            <div>
                <Toast ref={toast} />
                <div className="w-full" style={{ height: 'calc(100vh - 8rem)' }}>
                    <Loading loading={loading} onChangeLoading={() => setLoading(false)} />
                </div>
            </div>
        );
    }

    return (
        <div>
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between">
                <HeaderProgram selectedCompany={selectedCompany} moduleName="Mikrokom" />
            </div>
            <br />
            <h3 className="mb-5">Mikrokom Envanter</h3>

            <div className="card flex align-items-end justify-content-between flex-wrap gap-4">
                <div className="flex align-items-end gap-5 flex-wrap">
                    <div className="w-17rem relative">
                        <label htmlFor="mikrokom-inventory-company" className="mb-2 block">
                            Şirketler
                        </label>
                        <Dropdown
                            inputId="mikrokom-inventory-company"
                            dataKey="companyCode"
                            value={selectedCompanyId}
                            onChange={handleCompanyChange}
                            options={mikrokomCompanies}
                            optionLabel="companyName"
                            optionValue="companyCode"
                            placeholder="Şirket Seçiniz"
                            filter
                            className={classNames({ 'p-invalid': !selectedCompanyId, 'w-full': true })}
                            emptyMessage="Şirket Bulunamadı"
                        />
                        {!selectedCompanyId && (
                            <small className="p-error absolute left-0" style={{ bottom: '-18px' }}>
                                Şirket Seçilmedi
                            </small>
                        )}
                    </div>

                    <div className="w-17rem relative">
                        <label htmlFor="mikrokom-inventory-period" className="mb-2 block">
                            Defter Dönemi
                        </label>
                        <Dropdown
                            inputId="mikrokom-inventory-period"
                            value={selectedPeriodId}
                            onChange={handlePeriodChange}
                            options={periodOptions}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Dönem Seçiniz"
                            filter
                            disabled={!selectedCompanyId}
                            className={classNames({ 'p-invalid': selectedCompanyId && !selectedPeriodId, 'w-full': true })}
                            emptyMessage="Dönem Bulunamadı"
                        />
                        {selectedCompanyId && !selectedPeriodId && (
                            <small className="p-error absolute left-0" style={{ bottom: '-18px' }}>
                                Dönem Seçilmedi
                            </small>
                        )}
                    </div>

                    <div className="w-13rem">
                        <label htmlFor="mikrokom-inventory-type" className="mb-2 block">
                            Envanter Türü
                        </label>
                        <Dropdown
                            inputId="mikrokom-inventory-type"
                            value={envanterTur}
                            onChange={handleInventoryTypeChange}
                            options={inventoryTypeOptions}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Tür Seçiniz"
                            className="w-full"
                        />
                    </div>

                    <Button icon="pi pi-ticket" onClick={handleLoadInventory} className="px-4" label="Envanter Getir" disabled={!selectedCompanyId || !selectedPeriodId || !envanterTur || loading} loading={loading} />
                    <Button icon="pi pi-send" label="Gönder" severity="success" className="text-white" disabled={!sendEnabled} onClick={() => setSendDialogVisible(true)} loading={sendLoading} />
                </div>
                <div className="flex align-items-end gap-5">
                    <PortalHeader selectedCompany={selectedCompany} selectedCompanyCode={selectedCompanyId} moduleName="Mikrokom" />
                </div>
            </div>

            <div className="card">
                <DataTable
                    value={inventoryDetailRows}
                    dataKey="_rowKey"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{totalRecords} kayıttan {first} ila {last} arası gösteriliyor"
                    globalFilter={globalFilter}
                    header={detailHeader}
                    emptyMessage="Envanter kaydı bulunamadı"
                    className="p-datatable-gridlines"
                >
                    <Column field="AssetsDate" header="Tarih" sortable body={(rowData) => formatInventoryDate(rowData.AssetsDate)} style={{ minWidth: '9rem' }} />
                    <Column header="Ana Hesap" body={accountMainTemplate} sortable sortField="AccountMainID" style={{ minWidth: '16rem' }} />
                    <Column header="Alt Hesap" body={accountSubTemplate} sortable sortField="AccountSubID" style={{ minWidth: '16rem' }} />
                    <Column field="Amount" header="Tutar" sortable body={(rowData) => formatAmount(rowData.Amount)} style={{ minWidth: '10rem' }} />
                    <Column field="DetailComment" header="Detay Açıklama" sortable style={{ minWidth: '14rem' }} />
                    <Column header="Birim" body={measurableTemplate} sortable sortField="measurableUnitOfMeasure" style={{ minWidth: '10rem' }} />
                </DataTable>
            </div>

            <Dialog
                visible={sendDialogVisible}
                onHide={!sendLoading ? () => setSendDialogVisible(false) : () => {}}
                header="Envanter Gönder"
                modal
                style={{ width: '660px' }}
                footer={
                    <div className="flex justify-content-end flex-wrap gap-2">
                        <Button
                            icon="pi pi-check"
                            label={sendLoading ? 'Envanter Gönderiliyor...' : 'Envanter Defterini Taslaklara Kaydet'}
                            className="text-white"
                            onClick={() => handleSubmitSend(0)}
                            disabled={!enteredBy.trim() || sendLoading}
                            loading={sendLoading}
                        />

                        <Button
                            icon="pi pi-check"
                            label={sendLoading ? 'Envanter Gönderiliyor...' : 'Envanter Defterini Oluştur ve Portale Gönder'}
                            className="text-white"
                            onClick={() => handleSubmitSend(1)}
                            disabled={!enteredBy.trim() || sendLoading}
                            loading={sendLoading}
                        />
                    </div>
                }
            >
                <div className="flex flex-column gap-4 pt-2">
                    <div className="surface-100 border-round p-3 text-sm">
                        <div className="font-semibold">{selectedMikrokomCompany?.companyName}</div>
                        <div className="text-color-secondary mt-1">{inventoryRows.length} kayıt gönderime hazırlanıyor.</div>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="mikrokom-inventory-entered-by" className="font-medium">
                            Gönderen Kişi <span className="text-pink-500 font-bold">*</span>
                        </label>
                        <InputText id="mikrokom-inventory-entered-by" value={enteredBy} onChange={(event) => setEnteredBy(event.target.value)} placeholder="Gönderen kişi giriniz" />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
